/**
 * ehr#277 - tracked changes on a progress note. A reviewer suggests the full new text of a
 * section; the author sees it as a word-level diff against their own text (removed text struck
 * through in red, added text in green), with the reviewer's comments numbered by section.
 */
import type { NoteSuggestions } from '@ehr/shared';

export type DiffPart = { kind: 'same' | 'del' | 'add'; text: string };

// Above this many token pairs the middle is shown as one removal and one addition.
const MAX_CELLS = 4_000_000;

// Whitespace runs, words (letters, digits, apostrophes, hyphens) and single punctuation marks,
// so "Mondays." -> "Mondays; two..." marks only the punctuation and the new words.
const tokens = (s: string): string[] => s.match(/\s+|[\p{L}\p{N}'’-]+|[^\s\p{L}\p{N}'’-]/gu) ?? [];

/**
 * Word diff of `before` -> `after`. Whitespace runs are tokens too, so spacing and line breaks
 * survive; within a run of changes the removals come first, then the additions.
 */
export function wordDiff(before: string, after: string): DiffPart[] {
  const a = tokens(before);
  const b = tokens(after);
  let pre = 0;
  while (pre < a.length && pre < b.length && a[pre] === b[pre]) pre++;
  let suf = 0;
  while (
    suf < a.length - pre &&
    suf < b.length - pre &&
    a[a.length - 1 - suf] === b[b.length - 1 - suf]
  )
    suf++;
  const a2 = a.slice(pre, a.length - suf);
  const b2 = b.slice(pre, b.length - suf);
  const raw: DiffPart[] = [];
  const push = (kind: DiffPart['kind'], text: string) => raw.push({ kind, text });
  if (pre) push('same', a.slice(0, pre).join(''));
  const n = a2.length;
  const m = b2.length;
  if (n * m > MAX_CELLS) {
    if (n) push('del', a2.join(''));
    if (m) push('add', b2.join(''));
  } else if (n || m) {
    // Longest common subsequence of the middle, read off front to back.
    const w = m + 1;
    const lcs = new Uint32Array((n + 1) * w);
    for (let i = n - 1; i >= 0; i--)
      for (let j = m - 1; j >= 0; j--)
        lcs[i * w + j] =
          a2[i] === b2[j]
            ? lcs[(i + 1) * w + j + 1]! + 1
            : Math.max(lcs[(i + 1) * w + j]!, lcs[i * w + j + 1]!);
    let i = 0;
    let j = 0;
    while (i < n && j < m) {
      if (a2[i] === b2[j]) {
        push('same', a2[i]!);
        i++;
        j++;
      } else if (lcs[(i + 1) * w + j]! >= lcs[i * w + j + 1]!) push('del', a2[i++]!);
      else push('add', b2[j++]!);
    }
    while (i < n) push('del', a2[i++]!);
    while (j < m) push('add', b2[j++]!);
  }
  if (suf) push('same', a.slice(a.length - suf).join(''));
  return tidy(raw);
}

/**
 * Bare spaces kept between two changes join the change (so "a b" -> "x y" reads as one
 * struck phrase and one new one, not word by word); each run of changes becomes one removal
 * and one addition; neighbours of the same kind merge.
 */
function tidy(parts: DiffPart[]): DiffPart[] {
  const isChange = (p: DiffPart | undefined) => Boolean(p && p.kind !== 'same');
  const flat: DiffPart[] = [];
  parts.forEach((p, k) => {
    if (p.kind === 'same' && !p.text.trim() && isChange(parts[k - 1]) && isChange(parts[k + 1])) {
      flat.push({ kind: 'del', text: p.text }, { kind: 'add', text: p.text });
    } else flat.push(p);
  });
  const out: DiffPart[] = [];
  let del = '';
  let add = '';
  const flush = () => {
    if (del) out.push({ kind: 'del', text: del });
    if (add) out.push({ kind: 'add', text: add });
    del = '';
    add = '';
  };
  for (const p of flat) {
    if (p.kind === 'del') del += p.text;
    else if (p.kind === 'add') add += p.text;
    else {
      flush();
      const last = out[out.length - 1];
      if (last?.kind === 'same') last.text += p.text;
      else out.push({ ...p });
    }
  }
  flush();
  return out;
}

/** True when the suggestion changes anything in that text. */
export const hasChanges = (parts: DiffPart[]) => parts.some((p) => p.kind !== 'same');

/** Read body.suggestions as stored; null when absent or not the expected shape. */
export function readSuggestions(v: unknown): NoteSuggestions | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const s = v as Partial<NoteSuggestions>;
  const sections =
    s.sections && typeof s.sections === 'object' && !Array.isArray(s.sections) ? s.sections : {};
  const comments = Array.isArray(s.comments)
    ? s.comments.filter(
        (c) => c && typeof c.section === 'string' && typeof c.text === 'string' && c.text.trim(),
      )
    : [];
  if (!Object.keys(sections).length && !comments.length) return null;
  return {
    round: typeof s.round === 'number' ? s.round : 0,
    by: typeof s.by === 'string' ? s.by : '',
    at: typeof s.at === 'string' ? s.at : '',
    sections,
    comments,
  };
}

export type NumberedComment = NoteSuggestions['comments'][number] & { n: number };

/** Comments numbered in the note's section order (then oldest first). */
export function numberComments(
  order: string[],
  comments: NoteSuggestions['comments'],
): NumberedComment[] {
  const rank = (s: string) => {
    const i = order.indexOf(s);
    return i < 0 ? order.length : i;
  };
  return comments
    .map((c, i) => ({ c, i }))
    .sort((x, y) => rank(x.c.section) - rank(y.c.section) || x.i - y.i)
    .map(({ c }, k) => ({ ...c, n: k + 1 }));
}

/** The author's sections with the suggested text put in (Accept all). */
export function applySuggestions(
  body: Record<string, string>,
  s: NoteSuggestions,
): Record<string, string> {
  const next = { ...body };
  for (const [k, v] of Object.entries(s.sections)) if (typeof v === 'string') next[k] = v;
  return next;
}

/** How many sections the suggestion actually changes, against the author's text now. */
export function changeCount(body: Record<string, string>, s: NoteSuggestions): number {
  return Object.entries(s.sections).filter(
    ([k, v]) => typeof v === 'string' && v !== (body[k] ?? ''),
  ).length;
}

/**
 * ehr#571 (Chase 10-21, ask 7): the reviewer's comments as a list for the changes-requested
 * banner, numbered like the comment cards, each with its section's name so it can link there.
 * A comment on a section the note's template doesn't show keeps its key as the name.
 */
export function commentBullets(
  sections: [string, string][],
  comments: NoteSuggestions['comments'],
): (NumberedComment & { label: string })[] {
  const order = sections.map(([, k]) => k);
  return numberComments(order, comments).map((c) => ({
    ...c,
    label: sections.find(([, k]) => k === c.section)?.[0] ?? c.section,
  }));
}

// ── ehr#571 (reopen 10-23, PROGRESS-NOTE V141 V83): in-place review, one edit at a time ──────
// The stored shape is unchanged (body.suggestions keeps the reviewer's full text per section);
// each edit is a run of changes in the word diff of the author's text against it, so notes sent
// back before this change read the same way.

/** One edit: the words it removes and the words it adds (either may be empty). */
export type EditHunk = { i: number; del: string; add: string };

const runsOf = (parts: DiffPart[]) => {
  // the index of the run of changes each part belongs to (-1 for unchanged text)
  let n = -1;
  return parts.map((p, k) => {
    if (p.kind === 'same') return -1;
    if (k === 0 || parts[k - 1]!.kind === 'same') n++;
    return n;
  });
};

/** The reviewer's edits to one section, in reading order. */
export function editHunks(before: string, after: string): EditHunk[] {
  const parts = wordDiff(before, after);
  const runs = runsOf(parts);
  const out: EditHunk[] = [];
  parts.forEach((p, k) => {
    const r = runs[k]!;
    if (r < 0) return;
    const h = (out[r] ??= { i: r, del: '', add: '' });
    if (p.kind === 'del') h.del += p.text;
    else h.add += p.text;
  });
  return out;
}

/** Rebuild the text from the diff, taking the new words for the runs `take` says yes to. */
function rebuild(before: string, after: string, take: (run: number) => boolean): string {
  const parts = wordDiff(before, after);
  const runs = runsOf(parts);
  return parts
    .map((p, k) => {
      const r = runs[k]!;
      if (r < 0) return p.text;
      if (p.kind === 'del') return take(r) ? '' : p.text;
      return take(r) ? p.text : '';
    })
    .join('');
}

/** Drop sections whose suggestion now matches the author's text; null when nothing is left. */
function settle(body: Record<string, string>, s: NoteSuggestions): NoteSuggestions | null {
  const sections = Object.fromEntries(
    Object.entries(s.sections).filter(([k, v]) => typeof v === 'string' && v !== (body[k] ?? '')),
  ) as NoteSuggestions['sections'];
  if (!Object.keys(sections).length && !s.comments.length) return null;
  return { ...s, sections };
}

type EditResult = { body: Record<string, string>; suggestions: NoteSuggestions | null };

/** Keep: the author's text takes that one edit; the reviewer's other edits stay pending. */
export function keepEdit(
  body: Record<string, string>,
  s: NoteSuggestions,
  section: string,
  i: number,
): EditResult {
  const after = s.sections[section as keyof NoteSuggestions['sections']];
  const before = body[section] ?? '';
  if (typeof after !== 'string' || !editHunks(before, after)[i]) return { body, suggestions: s };
  const next = { ...body, [section]: rebuild(before, after, (r) => r === i) };
  return { body: next, suggestions: settle(next, s) };
}

/** Revert: the suggestion loses that one edit; the author's text stays as it is. */
export function revertEdit(
  body: Record<string, string>,
  s: NoteSuggestions,
  section: string,
  i: number,
): EditResult {
  const after = s.sections[section as keyof NoteSuggestions['sections']];
  const before = body[section] ?? '';
  if (typeof after !== 'string' || !editHunks(before, after)[i]) return { body, suggestions: s };
  const sections = { ...s.sections, [section]: rebuild(before, after, (r) => r !== i) };
  return { body, suggestions: settle(body, { ...s, sections }) };
}

/** Every edit still waiting on Keep or Revert, in the note's section order. */
export function pendingEdits(
  order: string[],
  body: Record<string, string>,
  s: NoteSuggestions | null,
): (EditHunk & { section: string })[] {
  if (!s) return [];
  return order.flatMap((k) => {
    const v = s.sections[k as keyof NoteSuggestions['sections']];
    return typeof v === 'string'
      ? editHunks(body[k] ?? '', v).map((h) => ({ ...h, section: k }))
      : [];
  });
}

/** Each run of changes as a character range of `before` and the text that replaces it. */
function opsOf(before: string, after: string): { from: number; to: number; ins: string }[] {
  const parts = wordDiff(before, after);
  const runs = runsOf(parts);
  const ops: { from: number; to: number; ins: string }[] = [];
  let at = 0;
  parts.forEach((p, k) => {
    const r = runs[k]!;
    if (r >= 0 && !ops[r]) ops[r] = { from: at, to: at, ins: '' };
    if (p.kind === 'same') at += p.text.length;
    else if (p.kind === 'del') {
      at += p.text.length;
      ops[r]!.to = at;
    } else ops[r]!.ins += p.text;
  });
  return ops;
}

/**
 * The author typed in a box the reviewer edited (`before` -> `after`): carry the reviewer's
 * edits over to the new text. An edit that touches the words the author changed is dropped
 * (the author rewrote that part themselves); the rest stay pending.
 */
export function rebaseSuggestion(before: string, after: string, suggested: string): string {
  if (before === after) return suggested;
  const mine = opsOf(before, after);
  const theirs = opsOf(before, suggested);
  const clash = (a: { from: number; to: number }, b: { from: number; to: number }) =>
    a.from === a.to || b.from === b.to
      ? Math.max(a.from, b.from) <= Math.min(a.to, b.to)
      : Math.max(a.from, b.from) < Math.min(a.to, b.to);
  let text = after;
  // right to left, so each earlier position still holds
  for (const t of theirs.filter((t) => !mine.some((m) => clash(m, t))).reverse()) {
    const shift = mine
      .filter((m) => m.to <= t.from)
      .reduce((n, m) => n + m.ins.length - (m.to - m.from), 0);
    text = text.slice(0, t.from + shift) + t.ins + text.slice(t.to + shift);
  }
  return text;
}
